/**
 * Represents a registry that stores key-value pairs.
 *
 * @template T The type of values stored in the registry.
 */
export interface Registry<T> {
    /**
     * Sets a value in the registry.
     * @param key The key to set.
     * @param value The value to associate with the key.
     * @returns The registry instance for chaining.
     */
    set: (key: string, value: T) => Registry<T>;

    /**
     * Resets the registry to its initial state.
     * @returns The registry instance for chaining.
     */
    reset: () => Registry<T>;

    /**
     * Removes a key-value pair from the registry.
     * @param key The key to remove.
     * @returns The registry instance for chaining.
     */
    unset: (key: string) => Registry<T>;

    /**
     * Retrieves a value from the registry.
     * @param key The key to look up.
     * @returns The value associated with the key, or undefined if not found.
     */
    get: (key: string) => T | undefined;

    /**
     * Retrieves all key-value pairs in the registry.
     * @returns An object containing all key-value pairs.
     */
    getAll: () => Record<string, T>;

    /**
     * Retrieves all values in the registry.
     * @returns An array of all values.
     */
    getAllValues: () => Array<T>;
}

/**
 * Creates a registry singleton that acts as a key-value store.
 * This registry can be used to store and inject behavior or configuration.
 *
 * @template T The type of values to be stored in the registry.
 * @param {Record<string, T>} [defaultItems] An optional object of default key-value pairs to initialize the registry.
 * @returns {Registry<T>} A Registry instance with methods to manipulate and retrieve stored values.
 *
 * @example
 * // Create a registry for configuration settings
 * const configRegistry = createRegistry<string>({
 *   apiUrl: 'https://api.example.com',
 *   timeout: '5000'
 * });
 *
 * // Add a new setting
 * configRegistry.set('maxRetries', '3');
 *
 * // Retrieve a setting
 * const apiUrl = configRegistry.get('apiUrl');
 *
 * // Get all settings
 * const allSettings = configRegistry.getAll();
 *
 * // Reset to default values
 * configRegistry.reset();
 *
 * @example
 * // Create a registry for feature flags
 * const featureFlagRegistry = createRegistry<boolean>();
 *
 * featureFlagRegistry
 *   .set('newUIEnabled', true)
 *   .set('betaFeatureEnabled', false);
 *
 * if (featureFlagRegistry.get('newUIEnabled')) {
 *   // Render new UI
 * }
 */
export const createRegistry = <T>(defaultItems?: Record<string, T>): Registry<T> => {
    const defaultValuesOrEmpty = defaultItems ? { ...defaultItems } : {};
    const currentState = { current: { ...defaultValuesOrEmpty } };

    return {
        set(key: string, value: T): Registry<T> {
            currentState.current[key] = value;
            return this;
        },
        reset(): Registry<T> {
            currentState.current = { ...defaultValuesOrEmpty };
            return this;
        },
        unset(key: string): Registry<T> {
            delete currentState.current[key];
            return this;
        },
        get: (key: string): T | undefined => {
            return currentState.current[key];
        },
        getAll: (): Record<string, T> => {
            return currentState.current;
        },
        getAllValues: (): Array<T> => {
            return Object.values(currentState.current);
        },
    };
};
